import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, ScanBarcode, X } from 'lucide-react';

const BarcodeScanner = ({ open, onClose, onDetected }) => {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReady(false); setError(null);
    const reader = new BrowserMultiFormatReader();
    let stopped = false;
    (async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        const back = devices.find(d => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
        const controls = await reader.decodeFromVideoDevice(back?.deviceId, videoRef.current, (result, err, ctrls) => {
          if (result && !stopped) {
            stopped = true;
            try { ctrls.stop(); } catch (e) { /* ignore */ }
            onDetected(result.getText());
            onClose();
          }
        });
        controlsRef.current = controls;
        setReady(true);
      } catch (e) {
        setError(e.message || 'Camera access failed');
      }
    })();
    return () => {
      stopped = true;
      try { controlsRef.current?.stop(); } catch (e) { /* ignore */ }
    };
  }, [open, onClose, onDetected]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-2"><ScanBarcode className="w-6 h-6" /></div>
          <DialogTitle className="font-display text-2xl text-emerald-950">Scan a barcode</DialogTitle>
          <DialogDescription>Point your camera at any packaged food barcode.</DialogDescription>
        </DialogHeader>
        <div className="relative m-6 rounded-2xl overflow-hidden bg-black aspect-[3/4]">
          <video ref={videoRef} className="w-full h-full object-cover" muted playsInline autoPlay />
          <div className="absolute inset-8 border-2 border-white/70 rounded-xl pointer-events-none" />
          {!ready && !error && (
            <div className="absolute inset-0 grid place-items-center bg-black/40 text-white text-sm"><Loader2 className="w-6 h-6 animate-spin mr-2" />Starting camera…</div>
          )}
          {error && (
            <div className="absolute inset-0 grid place-items-center bg-black/70 text-white text-sm px-6 text-center">{error}</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
